import React from 'react';

import { Alert, Card, CardContent, CircularProgress, Stack, TextField, Typography } from '@mui/material';

import type { PnrBooking } from '~/services/FlightsService';

import BusinessEmployeeSelect from './BusinessEmployeeSelect';
import PnrImportAncillaryFeeRow from './PnrImportAncillaryFeeRow';
import PnrImportApplyFeeToAll from './PnrImportApplyFeeToAll';
import {
  type HandlingFees,
  applyHandlingFeeToAll,
  calculateCustomerPrice,
  calculateHandlingFeeTotal,
  formatAncillaryFlight,
  formatBaggageQuantity,
  formatFlightStop,
  getPnrBaggages,
  getPnrBookingTotals,
  getPnrPaidSeats,
  getProviderCostLabel,
  isValidMargin,
  parseMargin,
} from './pnrImport';

interface Props {
  pnr: string;
  booking?: PnrBooking;
  isLoading: boolean;
  isError: boolean;
  /** The agent's margin over the provider cost, as typed. */
  margin: string;
  onMarginChange: (margin: string) => void;
  /** The handling fee the agent typed for each seat and extra baggage booked on the PNR, by Sabre ancillary id. */
  handlingFees: HandlingFees;
  onHandlingFeeChange: (ancillaryId: string, fee: string) => void;
  /** Sets the fees of several seats or baggage at once, leaving the others as they are. */
  onHandlingFeesChange: (handlingFees: HandlingFees) => void;
  /** Business whose profiles the travellers are matched to; set only for Business Traveller imports. */
  businessId?: string;
  travellerProfiles: Record<string, App.BusinessEmployee | null>;
  onTravellerProfileChange: (travellerId: string, employee: App.BusinessEmployee | null) => void;
  /** The order was already paid: the price is only recorded, the customer is not charged for it. */
  isPaid?: boolean;
  disabled?: boolean;
}

const CUSTOMER_PRICE_LABEL = 'Customer price';
const RECORDED_PRICE_LABEL = 'Recorded price (not charged, already paid)';

export default function PnrImportPnrCard({
  pnr,
  booking,
  isLoading,
  isError,
  margin,
  onMarginChange,
  handlingFees,
  onHandlingFeeChange,
  onHandlingFeesChange,
  businessId,
  travellerProfiles,
  onTravellerProfileChange,
  isPaid,
  disabled,
}: Props) {
  const { providerCost, currency } = getPnrBookingTotals(booking);
  const flights = booking?.bookingJourney?.legs?.flatMap((leg) => leg.flights ?? []) ?? [];
  const marginValue = parseMargin(margin);
  const hasValidMargin = isValidMargin(margin, providerCost);
  const paidSeats = getPnrPaidSeats(booking);
  const baggages = getPnrBaggages(booking);
  const handlingFeeTotal = calculateHandlingFeeTotal(handlingFees);
  let customerPrice = '';
  if (hasValidMargin && marginValue !== undefined && providerCost > 0) {
    customerPrice = calculateCustomerPrice(providerCost, marginValue, handlingFeeTotal).toFixed(2);
  }
  let priceLabel = CUSTOMER_PRICE_LABEL;
  if (isPaid) {
    priceLabel = RECORDED_PRICE_LABEL;
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1.5}>
          <Typography variant="h6">PNR {pnr}</Typography>
          {isLoading && <CircularProgress size={20} />}
          {isError && <Alert severity="error">Could not retrieve this booking from Sabre.</Alert>}
          {booking?.isTicketed && (
            <Alert severity="info">This booking is already ticketed. The order will have no payment deadline.</Alert>
          )}

          {flights.map((flight, index) => (
            <Typography key={index} variant="body2">
              {flight.marketingAirline?.code} {flight.flightNumber}:{' '}
              {formatFlightStop(flight.departureAirport, flight.departingDate, flight.departingTime)} →{' '}
              {formatFlightStop(flight.arrivalAirport, flight.arrivalDate, flight.arrivalTime)}
            </Typography>
          ))}

          {booking?.ticketingDeadline && (
            <Typography variant="body2" color="text.secondary">
              Ticketing deadline {booking.ticketingDeadline}
            </Typography>
          )}

          <Typography variant="subtitle2">Travellers</Typography>
          {booking?.travelers?.map((traveller) => {
            const travellerSeats = paidSeats.filter((seat) => seat.travelerId === traveller.id);
            const travellerBaggages = baggages.filter((baggage) => baggage.travelerId === traveller.id);
            return (
              <Stack key={traveller.id} spacing={1}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: 260 }}>
                    {[traveller.title, traveller.name, traveller.lastName].filter(Boolean).join(' ')} ({traveller.type})
                  </Typography>
                  {businessId && (
                    <BusinessEmployeeSelect
                      businessId={businessId}
                      label="Business profile (empty for a guest)"
                      value={travellerProfiles[traveller.id] ?? null}
                      onChange={(employee) => onTravellerProfileChange(traveller.id, employee)}
                      initialSearch={traveller.email}
                      disabled={disabled}
                    />
                  )}
                </Stack>
                {travellerSeats.map((seat) => (
                  <PnrImportAncillaryFeeRow
                    key={seat.id}
                    description={`${formatAncillaryFlight(seat, booking)} · seat ${seat.seatNumber}`}
                    price={seat.price}
                    fee={handlingFees[seat.id]}
                    onFeeChange={(fee) => onHandlingFeeChange(seat.id, fee)}
                    disabled={disabled}
                  />
                ))}
                {travellerBaggages.map((baggage) => (
                  <PnrImportAncillaryFeeRow
                    key={baggage.id}
                    description={`${formatAncillaryFlight(baggage, booking)} · ${formatBaggageQuantity(baggage.quantity)}`}
                    price={baggage.price}
                    fee={handlingFees[baggage.id]}
                    onFeeChange={(fee) => onHandlingFeeChange(baggage.id, fee)}
                    disabled={disabled}
                  />
                ))}
              </Stack>
            );
          })}

          {paidSeats.length > 0 && (
            <PnrImportApplyFeeToAll
              label="Handling fee for all seats"
              buttonLabel="Apply to all seats"
              onApply={(fee) => onHandlingFeesChange(applyHandlingFeeToAll(paidSeats, fee))}
              disabled={disabled}
            />
          )}
          {baggages.length > 0 && (
            <PnrImportApplyFeeToAll
              label="Handling fee for all baggage"
              buttonLabel="Apply to all baggage"
              onApply={(fee) => onHandlingFeesChange(applyHandlingFeeToAll(baggages, fee))}
              disabled={disabled}
            />
          )}

          <Typography variant="subtitle2">Price</Typography>
          <Typography variant="body2">
            {getProviderCostLabel(booking)} {currency} {providerCost.toFixed(2)}
          </Typography>
          <TextField
            size="small"
            type="number"
            label={`Margin (${currency ?? 'currency pending'})`}
            value={margin}
            onChange={(event) => onMarginChange(event.target.value)}
            slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
            error={margin !== '' && !hasValidMargin}
            required
            disabled={disabled}
            sx={{ maxWidth: 260 }}
          />
          {(paidSeats.length > 0 || baggages.length > 0) && (
            <Typography variant="body2">
              Handling fees {currency} {handlingFeeTotal.toFixed(2)}
            </Typography>
          )}
          {customerPrice && (
            <Typography variant="subtitle1">
              {priceLabel} {currency} {customerPrice}
            </Typography>
          )}
          {margin !== '' && !hasValidMargin && (
            <Alert severity="error">
              The margin must be an amount of zero or more with at most two decimals, and the customer price must be
              above zero.
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
